import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconDefinition, faUsersClass } from '@fortawesome/pro-duotone-svg-icons';
import { Select, Store } from '@ngxs/store';
import { SelectionEvent } from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { SortDescriptor } from '@progress/kendo-data-query';
import _ from 'lodash';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { getTopmostTableCellElement, isTargetingGridHeader } from 'src/app/helper/elementAncestorFunctions';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { shortcutStringBuilder } from 'src/app/helper/shortcutStringBuilder';
import { AgentListFilter } from 'src/app/samwebDescription/samweb/agentListFilterType';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { Key } from 'src/app/samwebDescription/samweb/shortcuts/Keys';
import { ShortcutDescription } from 'src/app/samwebDescription/samweb/shortcuts/shortcutDescription';
import { ShortcutTypeGlobal } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeGlobal';
import { GridSortSettingsChanged } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-sort-state/grid-sort.actions';
import { GridSortState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-sort-state/grid-sort.state';
import {
   AgentListAllFilterCleared,
   AgentListBrickSearchValueChanged,
   AgentListEntrySelected,
   AgentListSelectNextEntry,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-list-behaviour/agent-list-behaviour.actions';
import { AgentListBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-list-behaviour/agent-list-behaviour.state';
import { AgentListSettingState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/agent-list-setting/agent-list-setting.state';
import { DashboardState } from 'src/app/store/dashboard/dashboard.state';
import { StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { NextDialTargetChanged } from 'src/app/store/samwin/next-dial-target/next-dial-target.actions';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { SettingsSelectors } from 'src/app/store/settings/settings.selectors';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { AgentListSettingsComponent } from './agent-list-settings/agent-list-settings.component';

@Component({
   selector: 'samweb-agent-list-brick',
   templateUrl: './agent-list-brick.component.html',
   styleUrls: ['./agent-list-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'agentListComponentBrick',
   brickTitleTranslationKey: 'brick.brick_definitions.agent_list.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.agent_list.description',
   brickIcon: faUsersClass,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.GENERAL,
   settingStateHandler: AgentListSettingState,
   settingsComponent: AgentListSettingsComponent,
})
export class AgentListBrickComponent extends BasicBrickComponent implements OnInit, OnDestroy {
   get gridContent(): Element {
      return _(this.element.nativeElement.getElementsByClassName('k-grid-content')).head();
   }
   get gridDisplayHeight(): number {
      return this.element.nativeElement.querySelector('.k-virtual-content')?.offsetHeight;
   }
   get gridRowHeight(): number {
      return this.element.nativeElement.querySelector('td')?.offsetHeight ?? 0;
   }
   get gridPageSize(): number {
      return _.round(this.gridDisplayHeight / this.gridRowHeight);
   }

   constructor(private store: Store, private element: ElementRef) {
      super();
   }

   private destroy$ = new Subject();

   public agentList$: Observable<AgentListEntryRemoteDto[]>;
   public selectedKeys$: Observable<number[]>;
   public agentSearchValue$: Observable<string>;
   public activeFilters$: Observable<AgentListFilter[]>;
   public sortDescription$: Observable<SortDescriptor[]>;

   @Select(SamwinAgentListState.getPossibleAgentListFilters)
   public availableAgentStatusFilters$: Observable<AgentListFilter[]>;

   @ViewChild('cellTooltipDirective')
   public cellTooltipDirective: TooltipDirective;

   public keyActions: ShortcutDescription[] = [];
   public clearFilterIcon: IconDefinition = WellKnownIconsEnum.CLEAR.iconSmall;
   public filterIcon: IconDefinition = WellKnownIconsEnum.FILTER.icon;
   public statusBarFilterIcon: IconDefinition = WellKnownIconsEnum.FILTER.iconSmall;
   public isSearchExecuting$ = new BehaviorSubject<boolean>(true);
   public brickInstanceId: string;

   ngOnInit() {
      this.agentList$ = this.store.select(AgentListBehaviourState.getFilteredAndSortedAgentList).pipe(
         map((getFilteredAndSortedAgentList) => getFilteredAndSortedAgentList(this.brickInstanceId)),
         filter((list) => !_.isNil(list))
      );

      this.store
         .select(AgentListBehaviourState.isEntrySelected)
         .pipe(map((isEntrySelected) => isEntrySelected(this.brickInstanceId)))
         .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((isEntrySelected) =>
               this.agentList$.pipe(
                  tap((list) => {
                     if (!isEntrySelected && list.length > 0) {
                        this.store.dispatch(new AgentListEntrySelected(this.brickInstanceId, _(list).head().userId));
                        this.scrollToSelectedRow();
                     }
                  })
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.activeFilters$ = this.store.select(AgentListBehaviourState.getAgentListSelectedFilter).pipe(
         distinctUntilChanged(),
         map((getFilter) => getFilter(this.brickInstanceId))
      );

      this.agentSearchValue$ = this.store
         .select(AgentListBehaviourState.getAgentListSearchValue)
         .pipe(map((getAgentListSearchValue) => getAgentListSearchValue(this.brickInstanceId)));
      this.sortDescription$ = this.store.select(GridSortState.getBrickSortConfigurationArray).pipe(map((sorting) => sorting(this.brickInstanceId, 'default')));
      this.selectedKeys$ = this.store.select(AgentListBehaviourState.getSelectedId).pipe(
         map((getId) => getId(this.brickInstanceId)),
         map((selectedEntry) => [selectedEntry])
      );

      // publish Next Dial Target when the selected entry changes
      this.selectedKeys$
         .pipe(
            withLatestFrom(this.agentList$),
            distinctUntilChanged(([prevSelectedKeys, prevData], [curSelectedKeys, curData]) => {
               const prevSelectedEntry = _.find(prevData, (x) => x.userId === _.first(prevSelectedKeys));
               const curSelectedEntry = _.find(curData, (x) => x.userId === _.first(curSelectedKeys));
               return prevSelectedEntry?.directoryNumber === curSelectedEntry?.directoryNumber;
            }),
            tap(([selectedKeys, data]) => {
               const isFocussed = this.store.selectSnapshot(DashboardState.isBrickFocussedFn)(this.brickInstanceId);
               if (!isFocussed) {
                  return;
               }

               const selectedEntry = _.find(data, (x) => x.userId === _.first(selectedKeys));
               const agentSearchValue = this.store.selectSnapshot(AgentListBehaviourState.getAgentListSearchValue)(this.brickInstanceId);
               this.store.dispatch(
                  new NextDialTargetChanged(
                     {
                        number: _.isNil(selectedEntry) ? agentSearchValue : selectedEntry.directoryNumber,
                        name: selectedEntry?.displayName,
                     },
                     this.brickInstanceId
                  )
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // (re-)publish Next Dial Target on focus
      this.store
         .select(DashboardState.isBrickFocussedFn)
         .pipe(
            map((fn) => fn(this.brickInstanceId)),
            takeUntil(this.destroy$)
         )
         .subscribe((hasFocus) => {
            if (hasFocus) {
               const selectedEntry = this.store.selectSnapshot(AgentListBehaviourState.getSelectedEntry)(this.brickInstanceId);
               const agentSearchValue = this.store.selectSnapshot(AgentListBehaviourState.getAgentListSearchValue)(this.brickInstanceId);
               this.store.dispatch(
                  new NextDialTargetChanged(
                     {
                        number: _.isNil(selectedEntry) ? agentSearchValue : selectedEntry.directoryNumber,
                        name: selectedEntry?.displayName,
                     },
                     this.brickInstanceId
                  )
               );
            }
         });

      combineLatest([
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowUp),
            action: () => {
               console.debug('[HotKey] AgentList Pressed ArrowUp');
               this.store.dispatch(new AgentListSelectNextEntry(this.brickInstanceId, 'up'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowDown),
            action: () => {
               console.debug('[HotKey] AgentList Pressed ArrowDown');
               this.store.dispatch(new AgentListSelectNextEntry(this.brickInstanceId, 'down'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageUp),
            action: () => {
               console.debug('[HotKey] AgentList Pressed PageUp');
               this.store.dispatch(new AgentListSelectNextEntry(this.brickInstanceId, 'up', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageDown),
            action: () => {
               console.debug('[HotKey] AgentList Pressed PageDown');
               this.store.dispatch(new AgentListSelectNextEntry(this.brickInstanceId, 'down', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         this.getShortcutDescription$(this.store.select(SettingsSelectors.getHotkeyForAction).pipe(map((fn) => fn(ShortcutTypeGlobal.MAKE_CALL.value))), () => {
            const doDialAndAnswerShareHotkeys = this.store.selectSnapshot(SettingsSelectors.doDialAndAnswerShareHotkeys);
            const isCallRingingAndAcceptable = this.store.selectSnapshot(ClientPhoneState.isCallRingingAndAcceptable);
            if (doDialAndAnswerShareHotkeys && isCallRingingAndAcceptable) {
               return false;
            }
            console.debug('[HotKey] AgentList Pressed MAKE_CALL');
            const selectedNumber = this.store.selectSnapshot(AgentListBehaviourState.getSelectedAgentDn)(this.brickInstanceId);
            this.store.dispatch(new StartNewCallAction(selectedNumber));
            return true;
         }),
      ])
         .pipe(
            tap((shortcuts) => {
               this.keyActions = shortcuts;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // for triggering virtual scroll update on search
      combineLatest([
         this.agentSearchValue$.pipe(distinctUntilChanged((prev, curr) => _.isEqual(prev, curr))),
         this.activeFilters$.pipe(distinctUntilChanged((prev, curr) => _.isEqual(prev, curr))),
         this.store.select(AgentListSettingState.getIsShowOwnAgentEnabled).pipe(
            map((getIsShowOwnAgentEnabled) => getIsShowOwnAgentEnabled(this.brickInstanceId)),
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr))
         ),
         this.store.select(AgentListSettingState.getGroupIdsForAgentList).pipe(
            map((getGroupIdsForAgentList) => getGroupIdsForAgentList(this.brickInstanceId)),
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr))
         ),
      ])
         .pipe(
            tap(() => this.isSearchExecuting$.next(true)),
            debounceTime(50),
            tap(() => this.isSearchExecuting$.next(false)),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   onRowDoubleClick(event: MouseEvent) {
      if (isTargetingGridHeader(event)) {
         return;
      }

      const directoryNumber = this.store.selectSnapshot(AgentListBehaviourState.getSelectedAgentDn)(this.brickInstanceId);
      this.store.dispatch(new StartNewCallAction(directoryNumber));
   }

   searchTextChanged(value: string) {
      this.store.dispatch(new AgentListEntrySelected(this.brickInstanceId, null));
      this.store.dispatch(
         new NextDialTargetChanged(
            {
               number: value,
            },
            this.brickInstanceId
         )
      );
      this.store.dispatch(new AgentListBrickSearchValueChanged(value, this.brickInstanceId));
   }

   removeAllFilters() {
      this.store.dispatch(new AgentListEntrySelected(this.brickInstanceId, null));
      this.store.dispatch(new AgentListAllFilterCleared(this.brickInstanceId));
   }

   public sortChange(sortDescription: SortDescriptor[]) {
      this.store.dispatch(new GridSortSettingsChanged(this.brickInstanceId, _(sortDescription).head()));
   }

   selectionChanged(event: SelectionEvent) {
      const selectedRow = _.first(event.selectedRows);
      if (_.isNil(selectedRow)) {
         return;
      }
      const selectedEntry = selectedRow.dataItem as AgentListEntryRemoteDto;
      this.store.dispatch(new AgentListEntrySelected(this.brickInstanceId, selectedEntry.userId));
      this.store.dispatch(
         new NextDialTargetChanged(
            {
               number: selectedEntry.directoryNumber,
               name: selectedEntry.displayName,
            },
            this.brickInstanceId
         )
      );
   }

   scrollToSelectedRow() {
      const index = this.store.selectSnapshot(AgentListBehaviourState.getSelectedIndex)(this.brickInstanceId);
      const selectedRowPosition = this.gridRowHeight * index;
      const halfWindowHeight = this.gridDisplayHeight / 2 - this.gridRowHeight / 2;
      const scrollPosition = selectedRowPosition - halfWindowHeight;
      this.gridContent.scrollTo({ top: scrollPosition });
   }

   public showTooltip(e: MouseEvent): void {
      const element = e.target as HTMLElement;
      const tooltipAnchor = getTopmostTableCellElement(element);
      if (tooltipAnchor && (tooltipAnchor.scrollWidth > tooltipAnchor.parentElement.offsetWidth || tooltipAnchor.offsetWidth < tooltipAnchor.scrollWidth)) {
         this.cellTooltipDirective.toggle(tooltipAnchor);
      } else {
         this.cellTooltipDirective.hide();
      }
   }

   public hideTooltip() {
      this.cellTooltipDirective.hide();
   }

   public call(toCall: string) {
      this.store.dispatch(new StartNewCallAction(toCall));
   }
}
